const envValue = (...keys) =>
  keys.map((key) => process.env[key]).find((value) => String(value || "").trim()) || "";

const trimTrailingSlash = (value = "") => String(value || "").replace(/\/+$/, "");
const requiredEnvValue = (label, ...keys) => {
  const value = envValue(...keys);
  if (!value) {
    throw new Error(`${label} is required. Set it in the environment .env file for this release.`);
  }
  return value;
};

export const O_CLIENT_APP =
  requiredEnvValue("Application code", "REACT_APP_APP_CODE", "REACT_APP_CLIENT_APP_HEADER_VALUE", "VITE_APP_CODE", "VITE_CLIENT_APP_HEADER_VALUE");
export const AUTHORIZATION_HEADER_KEY = "Authorization";
export const ACTOR_ID_HEADER_KEY = "O-ACTOR-ID";
export const CLIENT_NAME_HEADER_KEY = "O-CLIENT-NAME";
export const CLIENT_NAME_HEADER_VALUE =
  envValue("REACT_APP_CLIENT_NAME_HEADER_VALUE", "VITE_CLIENT_NAME_HEADER_VALUE") ||
  O_CLIENT_APP;
export const O_API_KEY = envValue("REACT_APP_API_KEY", "VITE_GATEWAY_API_KEY");

// Require the release environment to be explicitly set; fail if missing.
const ENV = envValue("REACT_APP_ENV", "APP_ENV", "VITE_APP_ENV");
if (!ENV) {
  throw new Error(
    "REACT_APP_ENV environment variable is required. " +
      "Set it via .env.local (e.g., REACT_APP_ENV=dev) or a release .env file."
  );
}

export const APP_ENV = ENV;
export const API_BASE_URL = trimTrailingSlash(
  envValue("REACT_APP_API_BASE_URL", "REACT_APP_GATEWAY_URL", "VITE_BASE_URL", "VITE_GATEWAY_URL"),
) ||
  (() => {
    const protocol = envValue("REACT_APP_API_PROTOCOL", "VITE_APP_API_PROTOCOL") || "https";
    const host = envValue("REACT_APP_API_HOST", "VITE_APP_API_HOST");
    return host ? `${protocol}://${host}` : "";
  })();
if (!API_BASE_URL) {
  throw new Error(
    "REACT_APP_API_BASE_URL is required. Set it in the environment .env file for this release."
  );
}
export const API_HOST = (() => {
  try {
    return new URL(API_BASE_URL).host;
  } catch {
    return "";
  }
})();
export const OVIZIA_APP_BASE_URL =
  trimTrailingSlash(envValue("REACT_APP_OVIZIA_APP_BASE_URL", "VITE_OVIZIA_APP_BASE_URL")) ||
  API_BASE_URL;
export const OVIZIA_APP_HOST = (() => {
  try {
    return new URL(OVIZIA_APP_BASE_URL).host;
  } catch {
    return "";
  }
})();
export const APP_BASE_URL =
  trimTrailingSlash(envValue("REACT_APP_APP_BASE_URL", "VITE_APP_BASE_URL")) ||
  API_BASE_URL;
export const PUBLIC_SITE_URL =
  trimTrailingSlash(envValue("REACT_APP_PUBLIC_SITE_URL", "VITE_PUBLIC_SITE_URL")) ||
  API_BASE_URL;
export const PUBLIC_SITE_PREVIEW_BASE_URL =
  trimTrailingSlash(envValue("REACT_APP_PUBLIC_SITE_PREVIEW_BASE_URL", "VITE_PUBLIC_SITE_PREVIEW_BASE_URL")) ||
  PUBLIC_SITE_URL;
export const APPLE_CLIENT_ID = envValue("REACT_APP_APPLE_CLIENT_ID", "VITE_APP_APPLE_CLIENT_ID", "VITE_APPLE_CLIENT_ID");
export const PRODUCT_LINKS = {
  olreadia: trimTrailingSlash(envValue("REACT_APP_OLREADIA_PRODUCT_URL", "VITE_OLREADIA_PRODUCT_URL")),
  ovacus: trimTrailingSlash(envValue("REACT_APP_OVACUS_PRODUCT_URL", "VITE_OVACUS_PRODUCT_URL")),
  ovizia: trimTrailingSlash(envValue("REACT_APP_OVIZIA_PRODUCT_URL", "VITE_OVIZIA_PRODUCT_URL")) || OVIZIA_APP_BASE_URL,
  olitask: trimTrailingSlash(envValue("REACT_APP_OLITASK_PRODUCT_URL", "VITE_OLITASK_PRODUCT_URL")),
};

export const CORE_API_BASE_URL = `${API_BASE_URL}/api/v1`;
export const IDENTITY_SERVICE_BASE_URL = `${API_BASE_URL}/identity-service`;
export const OLITASK_SERVICE_BASE_URL = `${API_BASE_URL}/olitask-service`;
export const PAYMENT_SERVICE_BASE_URL = `${API_BASE_URL}/payment-service`;
export const PM_SERVICE_BASE_URL = `${API_BASE_URL}/pm-service`;
export const CHAT_SERVICE_BASE_URL = `${API_BASE_URL}/chat-service`;
export const PROFILE_SERVICE_BASE_URL = `${API_BASE_URL}/profile-service`;
export const OLREADIA_SERVICE_BASE_URL = `${API_BASE_URL}/olreadia-service`;
const configuredCareServiceBaseUrl = process.env.REACT_APP_CARE_SERVICE_BASE_URL;
export const resolveCareServiceBaseUrl = (
  configuredUrl,
  apiBaseUrl = API_BASE_URL,
) => {
  if (!configuredUrl) return `${apiBaseUrl}/care-service`;
  if (configuredUrl.startsWith("/")) {
    return `${apiBaseUrl}${configuredUrl}`;
  }
  return configuredUrl;
};
export const CARE_SERVICE_BASE_URL = resolveCareServiceBaseUrl(configuredCareServiceBaseUrl);
export const OVIZIA_SERVICE_BASE_URL = `${API_BASE_URL}/ovizia-service`;
export const COMPOSER_SERVICE_BASE_URL = OVIZIA_SERVICE_BASE_URL;
export const STORAGE_BASE_URL = `${API_BASE_URL}/storage-service`;
export const COMMUNICATION_SERVICE_BASE_URL = `${API_BASE_URL}/communication-service`;
export const NOTIFICATION_SERVICE_BASE_URL = `${API_BASE_URL}/notification-service`;
export const EMAIL_SERVICE_BASE_URL = `${API_BASE_URL}/email-service`;

// Mirrors `com.oliveeb.gateway.model.OliveebApp` (gateway-service).
// Backend serializes these enums as the lowercase, hyphenated strings via `@JsonValue`.
export const OLIVEEB_APP_VALUES = [
  // ===== Frontend Applications =====
  "olitask-mobile-ui",
  "olitask-web-ui",
  "ovacus-mobile-ui",
  "ovacus-web-ui",
  "ovizia-mobile-ui",
  "ovizia-web-ui",
  "olreadia-mobile-ui",
  "olreadia-web-ui",
  "oliveeb-admin-portal-web-ui",
  "oliveeb-postman-internal",
  "care-web-ui",

  // ===== Core Backend Services =====
  "olitask-main-service",
  "oliveeb-gateway-service",
  "oliveeb-identity-service",
  "oliveeb-profile-service",
  "oliveeb-payment-service",
  "oliveeb-storage-service",
  "oliveeb-notification-service",
  "oliveeb-chat-service",
  "oliveeb-communication-service",
  "oliveeb-email-service",
  "oliveeb-code-service",
  "oliveeb-cache-service",
  "oliveeb-social-service",
  "oliveeb-pm-service",
  "oliveeb-ai-service",
  "anonymous-service",
];

export const OLIVEEB_APP_OPTIONS = OLIVEEB_APP_VALUES.map((value) => ({
  value,
  label: value,
}));

const chargeItemSubscriptionCode =
  envValue("REACT_APP_CHARGE_ITEM_SUBSCRIPTION_CODE", "VITE_CHARGE_ITEM_SUBSCRIPTION_CODE") ||
  "OLREADIA_APP_SUBSCRIPTION";
const chargeItemSubscriptionRefundCode =
  envValue("REACT_APP_CHARGE_ITEM_SUBSCRIPTION_REFUND_CODE", "VITE_CHARGE_ITEM_SUBSCRIPTION_REFUND_CODE") ||
  "OLREADIA_APP_SUBSCRIPTION_REFUND";
const chargeItemSubscriptionLabel =
  envValue("REACT_APP_CHARGE_ITEM_SUBSCRIPTION_LABEL", "VITE_CHARGE_ITEM_SUBSCRIPTION_LABEL") ||
  "Olreadia App Subscription";
const chargeItemSubscriptionRefundLabel =
  envValue("REACT_APP_CHARGE_ITEM_SUBSCRIPTION_REFUND_LABEL", "VITE_CHARGE_ITEM_SUBSCRIPTION_REFUND_LABEL") ||
  "Olreadia App Subscription Refund";

export const CHARGE_ITEM_CODES = {
  OLREADIA_APP_SUBSCRIPTION: "OLREADIA_APP_SUBSCRIPTION",
  OLREADIA_APP_SUBSCRIPTION_REFUND: "OLREADIA_APP_SUBSCRIPTION_REFUND",
  OVACUS_APP_SUBSCRIPTION: "OVACUS_APP_SUBSCRIPTION",
  OVACUS_APP_SUBSCRIPTION_REFUND: "OVACUS_APP_SUBSCRIPTION_REFUND",
  SUBSCRIPTION: chargeItemSubscriptionCode,
  SUBSCRIPTION_REFUND: chargeItemSubscriptionRefundCode,
};

export const CHARGE_ITEM_LABELS = {
  [CHARGE_ITEM_CODES.OLREADIA_APP_SUBSCRIPTION]: "Olreadia App Subscription",
  [CHARGE_ITEM_CODES.OLREADIA_APP_SUBSCRIPTION_REFUND]: "Olreadia App Subscription Refund",
  [CHARGE_ITEM_CODES.OVACUS_APP_SUBSCRIPTION]: "Ovacus App Subscription",
  [CHARGE_ITEM_CODES.OVACUS_APP_SUBSCRIPTION_REFUND]: "Ovacus App Subscription Refund",
  [chargeItemSubscriptionCode]: chargeItemSubscriptionLabel,
  [chargeItemSubscriptionRefundCode]: chargeItemSubscriptionRefundLabel,
};

export const APP_LINKS = {
  ios: "https://appstoreconnect.apple.com/teams/767e23a7-ecf5-4a22-815e-5525630bddd7/apps/6753580996/testflight/ios",
  android: "https://play.google.com/apps/internaltest/4701619456935802876",
};

export const SMART_APP_LINK = APP_LINKS.both;

export const FEATURE_PROJECT_TEAM_MANAGEMENT = true;
